/**
 * @module engine/factory
 * @summary Shape creation + id-counter synchronisation.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 976-990 (syncShapeCounter)
 *   and 3434-3442 (mkShape).
 */
import type { Shape } from '../types';
import { S } from './state';
import { snap } from './coords';
import { registry } from './shapes/registry';

/**
 * Bring `S.shapeCounter` past the highest existing numeric id so newly created
 * shapes/connections never collide (important after loading a JSON file).
 * @ported-from lines 976-990.
 */
export function syncShapeCounter(): void {
  let max = 0;
  const scan = (arr: { id?: string }[] | undefined) => {
    if (!arr) return;
    for (const item of arr) {
      if (item && typeof item.id === 'string') {
        const m = item.id.match(/^[sc](\d+)$/);
        if (m) {
          const n = parseInt(m[1], 10);
          if (n > max) max = n;
        }
      }
    }
  };
  scan(S.shapes);
  scan(S.connections);
  S.shapeCounter = Math.max(S.shapeCounter, max);
}

/** Create a new shape of `type` centred on world point (wx, wy). */
export function mkShape(type: string, wx: number, wy: number): Shape {
  const def = registry.getDef(type) || { w: 60, h: 40, label: type, g: 'Custom' };
  const existing = new Set(S.shapes.map((s) => s.id));
  let newId: string;
  do {
    newId = 's' + ++S.shapeCounter;
  } while (existing.has(newId));
  return {
    id: newId,
    type,
    x: snap(wx - def.w / 2),
    y: snap(wy - def.h / 2),
    w: def.w,
    h: def.h,
    label: def.label,
    tag: '',
    labelPos: 'bottom',
    fillColor: '#ffffff',
    strokeColor: '#1a1a1a',
    strokeWidth: 1.5,
    fontSize: 10,
    notes: '',
    zIndex: S.shapeCounter,
  };
}
